import React, { useEffect, useState } from "react";
import { Route, Switch, useParams } from "react-router-dom";
import {
  useGetAdviseeActionPlans,
  useGetAdviseeCashFlowSummary,
  useGetAdviseeDetails,
  useGetAdviseeEstatePlanning,
  useGetAdviseeFutureReviews,
  useGetAdviseeInvestmentPortfolio,
  useGetAdviseePersonalInfo,
  useGetAdviseeReporting,
} from "apis/advisee";

import Personal from "./Personal/Personal";
import Policies from "./Policies/Policies";
import CashFlow from "./CashFlow/CashFlow";
import InvestmentPortfolio from "./InvestmentPortfolio/InvestmentPortfolio";
import NavReprogram from "./NavReprogram/NavReprogram";
import EstatePlanning from "./EstatePlanning/EstatePlanning";
import ActionPlan from "./ActionPlan/ActionPlan";
import FutureReview from "./FutureReview/FutureReview";

import {
  buildAdviseePersonalInfoUri,
  buildAdviseePoliciesUri,
  buildAdviseeCashFlowUri,
  buildAdviseeEstatePlanningUri,
  buildAdviseeInvestmentPortfolioUri,
  buildAdviseeNavReprogramUri,
  buildAdviseeActionPlanUri,
  buildAdviseeFutureReviewUri,
} from "utils/routes";
import { useGetUserById } from "apis/user";
import { getUserAuthObj } from "utils/auth";
import { useFetchConstant } from "apis/constant";
import { getLabelOfConstant } from "utils/general";
import { getGlobal } from "apis/global";

const Advisee = () => {
  const jwt = getUserAuthObj();
  const { id: adviseeId } = useParams();
  const [contactLoading, setContactLoading] = useState(false);
  const {
    isLoading: isAdviseeSummaryLoading,
    data: summaryInfo,
  } = useGetAdviseePersonalInfo(adviseeId);
  const {
    isLoading: isAdviseeDetailsLoading,
    data: personalDetails,
  } = useGetAdviseeDetails(adviseeId);
  const {
    isLoading: isReportingLoading,
    data: reportingDetails,
  } = useGetAdviseeReporting(adviseeId);
  const {
    isLoading: isCashFlowDetailsLoading,
    data: cashFlowDetails,
  } = useGetAdviseeCashFlowSummary(adviseeId);
  const { data: estatePlanningDetails } = useGetAdviseeEstatePlanning(
    adviseeId
  );
  const { data: investmentPortfolio } = useGetAdviseeInvestmentPortfolio(
    adviseeId
  );
  const { data: actionPlans } = useGetAdviseeActionPlans(adviseeId);
  const { data: futureReviews } = useGetAdviseeFutureReviews(adviseeId);
  const { data: userInfo, isLoading: userInfoLoading } = useGetUserById(
    jwt._id
  );
  const { isLoading: contactTypeLoading, data: contactType } = useFetchConstant(
    "contactType"
  );

  const isPersonalContentsLoading =
    isAdviseeSummaryLoading ||
    isAdviseeDetailsLoading ||
    isReportingLoading ||
    contactTypeLoading ||
    userInfoLoading ||
    contactLoading;

  const isPoliciesContentsLoading =
    isAdviseeSummaryLoading || isAdviseeDetailsLoading;

  const [contactList, setContactList] = useState([]);

  const loadDefaultTemplate = async () => {
    try {
      const res = await getGlobal();
      console.log(res, 23);
      if (res.data) {
        return res.data;
      }
      return [];
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    loadDefaultTemplate();
  }, []);

  useEffect(() => {
    generateContactList();
  }, [userInfo, contactType, personalDetails]);

  const generateContactList = async () => {
    // 1. Checking contactType format
    if (
      !Array.isArray(contactType) ||
      contactTypeLoading ||
      !userInfo ||
      !userInfo.company ||
      !personalDetails.policies
    ) {
      return;
    }

    var buffer = [];
    const policies = personalDetails.policies;
    setContactLoading(true);

    // 2. Select custom or default list
    if (userInfo.contactList && userInfo.contactList.length > 0) {
      buffer = userInfo.contactList;
    } else {
      buffer = await loadDefaultTemplate();
    }

    buffer = [
      // {
      //   role: "Personal Financial & Estate Planner",
      //   nameOrCompany:
      //     userInfo.name.firstName + "\xa0" + userInfo.name.lastName,
      //   // nameOrCompany: userInfo.company.name,
      //   email: userInfo.email,
      //   website: "",
      //   contact: `${userInfo.contact.countryCode}${userInfo.contact.contactNumber}`,
      //   officeNumber: "",
      //   faxNumber: "",
      // },
      ...buffer,
    ];

    // 3. Label lookup
    buffer = buffer.map((x) => {
      if (getLabelOfConstant(x.role, contactType, "code")) {
        return {
          ...x,
          role: getLabelOfConstant(x.role, contactType, "code"),
        };
      } else {
        return {
          ...x,
        };
      }
    });

    // 4. Filter to allow other role aside than default constant & filter out no relavent contact.
    buffer = buffer.filter(
      (contact) =>
        contactType.filter((type) => type.label === contact.role).length ===
          0 ||
        policies.filter(
          (policy) =>
            policy.status === "In Force" &&
            policy.company === contact.nameOrCompany
        ).length > 0
    );

    setContactList(buffer);
    setContactLoading(false);
  };

  return (
    <>
      <Switch>
        <Route path={buildAdviseePersonalInfoUri(adviseeId)}>
          <Personal
            isLoading={isPersonalContentsLoading}
            summaryInfo={summaryInfo}
            personalDetails={personalDetails}
            reportingDetails={reportingDetails}
            cashFlowDetails={cashFlowDetails}
            estatePlanningDetails={estatePlanningDetails}
            investmentPortfolio={investmentPortfolio}
            actionPlans={actionPlans}
            futureReviews={futureReviews}
            contactList={contactList ? contactList : []}
          />
        </Route>
        <Route path={buildAdviseePoliciesUri(adviseeId)}>
          <Policies
            isLoading={isPoliciesContentsLoading}
            summaryInfo={summaryInfo}
          />
        </Route>
        <Route path={buildAdviseeCashFlowUri(adviseeId)}>
          <CashFlow
            isLoading={isCashFlowDetailsLoading}
            cashFlowDetails={cashFlowDetails}
          />
        </Route>
        <Route path={buildAdviseeEstatePlanningUri(adviseeId)}>
          <EstatePlanning
            estatePlanningDetails={estatePlanningDetails}
            summaryPersonalInfo={summaryInfo.personalInfo}
          />
        </Route>
        <Route path={buildAdviseeInvestmentPortfolioUri(adviseeId)}>
          <InvestmentPortfolio
            summaryPersonalInfo={summaryInfo.personalInfo}
            investmentPortfolioDetails={investmentPortfolio}
          />
        </Route>
        <Route path={buildAdviseeNavReprogramUri(adviseeId)}>
          <NavReprogram
            summaryInfo={summaryInfo}
            currentReporting={reportingDetails}
            proposals={personalDetails.reprogramProposals}
          />
        </Route>
        <Route path={buildAdviseeActionPlanUri(adviseeId)}>
          <ActionPlan actionPlans={actionPlans} />
        </Route>
        <Route path={buildAdviseeFutureReviewUri(adviseeId)}>
          <FutureReview futureReviews={futureReviews} />
        </Route>
      </Switch>
    </>
  );
};

export default Advisee;
